import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  IndianRupee, CheckCircle2, Clock, ChevronDown, ChevronRight, FileText,
  Download, Loader2, RotateCcw, Handshake, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = () => new Date().toISOString().slice(0, 7);
const weekStartStr = () => { const d = new Date(); const off = (d.getDay() + 6) % 7; d.setDate(d.getDate() - off); return d.toISOString().slice(0, 10); };
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const METHODS = [["cash", "Cash"], ["upi", "UPI"], ["bank_transfer", "Bank Transfer"], ["other", "Other"]];
const methodLabel = (m) => (METHODS.find((x) => x[0] === m) || [m, m])[1];

function SumCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? "bg-gold-muted gold-border" : "bg-card border border-border"}`} data-testid={`settle-card-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <Icon className="gold-text" size={20} />
      <div className="mt-3 text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    fully_settled: ["Fully Settled", "bg-emerald-500/15 text-emerald-400"],
    partial: ["Partial", "bg-amber-500/15 text-amber-400"],
    unsettled: ["Unsettled", "bg-secondary text-muted-foreground"],
    reversal: ["Reversal", "bg-destructive/15 text-destructive"],
  };
  const [t, c] = map[status] || [status, "bg-secondary"];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${c}`}>{t}</span>;
}

export function SettlementManager() {
  const { user } = useAuth();
  const canSettle = ["super_admin", "owner"].includes(user?.role);
  const canReverse = user?.role === "super_admin";

  const [periodType, setPeriodType] = useState("monthly");
  const [month, setMonth] = useState(monthStr());
  const [week, setWeek] = useState(weekStartStr());
  const [custStart, setCustStart] = useState(todayStr());
  const [custEnd, setCustEnd] = useState(todayStr());
  const [locationId, setLocationId] = useState("all");
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [settleOpen, setSettleOpen] = useState(false);
  const [hStatus, setHStatus] = useState("all");
  const [hMethod, setHMethod] = useState("all");

  const params = useCallback(() => {
    if (periodType === "monthly") return { period_type: "monthly", start: `${month}-01`, location_id: locationId };
    if (periodType === "weekly") return { period_type: "weekly", start: week, location_id: locationId };
    return { period_type: "custom", start: custStart, end: custEnd, location_id: locationId };
  }, [periodType, month, week, custStart, custEnd, locationId]);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/settlement/summary", { params: params() }); setSummary(data); }
    catch (e) { toast.error(apiError(e)); }
  }, [params]);

  const loadHistory = useCallback(async () => {
    const p = { location_id: locationId };
    if (hStatus !== "all") p.status = hStatus;
    if (hMethod !== "all") p.payment_method = hMethod;
    try { const { data } = await api.get("/settlement/history", { params: p }); setHistory(data); }
    catch (e) { toast.error(apiError(e)); }
  }, [locationId, hStatus, hMethod]);

  useEffect(() => { api.get("/settlement/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const refresh = () => { loadSummary(); loadHistory(); };

  const exportCsv = async () => {
    try {
      const res = await api.get("/settlement/statement.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `settlement_${summary?.start_date}_${summary?.end_date}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openStatement = async () => {
    if (!summary) return;
    let bucket = [];
    try {
      const { data } = await api.get("/settlement/history", { params: { period_type: summary.period_type, location_id: locationId } });
      bucket = data.filter((h) => h.start_date === summary.start_date && h.end_date === summary.end_date && (h.location_id || null) === (summary.location_id || null));
    } catch { /* ignore, statement still renders totals */ }
    const rows = summary.by_location.flatMap((L) => L.chairs.map((c) => `<tr><td>${esc(L.name)}</td><td>${esc(c.chair_code)}</td><td style="text-align:right">${c.sessions}</td><td style="text-align:right">${fmt(c.revenue)}</td></tr>`)).join("");
    const hist = bucket.map((h) => `<tr><td>${esc(h.settlement_id)}</td><td>${esc(fmtDate(h.settlement_date))}</td><td style="text-align:right">${fmt(h.settled_amount)}</td><td>${esc(methodLabel(h.payment_method))}</td><td>${esc(h.payment_reference || "—")}</td><td>${esc(h.status)}</td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Settlement Statement</title><style>
      body{font-family:Georgia,serif;color:#111;padding:40px;max-width:820px;margin:auto}
      h1{color:#8a6d1a;margin:0} .sub{color:#666;margin:4px 0 20px}
      table{width:100%;border-collapse:collapse;margin:14px 0} td,th{border:1px solid #ddd;padding:8px;font-size:13px;text-align:left}
      th{background:#faf6ec} .totals td{border:none;padding:4px 0;font-size:14px} .totals .v{text-align:right;font-weight:bold}
      .box{border:2px solid #E2B857;border-radius:12px;padding:20px;margin-bottom:20px}
    </style></head><body>
      <h1>King's Professional Salon – Smart Massage</h1>
      <div class="sub">Settlement Statement · ${esc(summary.location_scope)}</div>
      <div class="box"><table class="totals">
        <tr><td>Settlement Period</td><td class="v">${esc(fmtDate(summary.start_date))} – ${esc(fmtDate(summary.end_date))}</td></tr>
        <tr><td>Total Revenue</td><td class="v">${fmt(summary.total_revenue)}</td></tr>
        <tr><td>My Share (${summary.my_share_percentage}%)</td><td class="v">${fmt(summary.my_share_amount)}</td></tr>
        <tr><td>Partner Share (${summary.partner_share_percentage}%)</td><td class="v">${fmt(summary.partner_share_amount)}</td></tr>
        <tr><td>Previously / Already Settled</td><td class="v">${fmt(summary.already_settled)}</td></tr>
        <tr><td>Remaining Pending</td><td class="v">${fmt(summary.pending)}</td></tr>
      </table></div>
      <h3>Revenue by Chair</h3>
      <table><thead><tr><th>Location</th><th>Chair</th><th style="text-align:right">Sessions</th><th style="text-align:right">Revenue</th></tr></thead><tbody>${rows || '<tr><td colspan="4">No revenue in this period.</td></tr>'}</tbody></table>
      <h3>Settlement Payments</h3>
      <table><thead><tr><th>ID</th><th>Date</th><th style="text-align:right">Amount</th><th>Method</th><th>Reference</th><th>Status</th></tr></thead><tbody>${hist || '<tr><td colspan="6">No settlements recorded yet.</td></tr>'}</tbody></table>
      <p style="color:#666;font-size:12px;margin-top:24px">Generated ${esc(new Date().toLocaleString("en-IN"))}. Revenue reflects paid sessions only; settlements are payments of the calculated share.</p>
      <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  const reverse = async (h) => {
    if (!window.confirm(`Reverse settlement ${h.settlement_id}? This creates a reversal record.`)) return;
    try { await api.post(`/settlement/${h.id}/reverse`); toast.success("Settlement reversed"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!summary) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div>;

  const periodLabel = `${fmtDate(summary.start_date)} – ${fmtDate(summary.end_date)}`;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div><div className="overline-label">Owner / Partner</div><h1 className="mt-1 font-serif text-2xl font-bold">Share Settlement</h1></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-1.5 gold-border text-gold" onClick={openStatement} data-testid="view-statement-btn"><FileText size={16} /> View Statement</Button>
          <Button variant="outline" className="gap-1.5 gold-border text-gold" onClick={exportCsv} data-testid="export-statement-btn"><Download size={16} /> Export CSV</Button>
          {canSettle && <Button className="gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" onClick={() => setSettleOpen(true)} data-testid="settle-amount-btn"><Handshake size={16} /> Settle Amount</Button>}
        </div>
      </div>

      {/* Period + Location filters */}
      <div className="mb-5 flex flex-col gap-3 rounded-2xl bg-card p-5 gold-border lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1.5 block">Settlement Period</Label>
            <div className="flex gap-1 rounded-lg bg-secondary p-1">
              {["weekly", "monthly", "custom"].map((p) => (
                <button key={p} onClick={() => setPeriodType(p)} data-testid={`period-${p}`}
                  className={`rounded-md px-4 py-1.5 text-sm capitalize transition-colors ${periodType === p ? "bg-gold font-semibold text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{p}</button>
              ))}
            </div>
          </div>
          {periodType === "monthly" && <div><Label>Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5" data-testid="month-input" /></div>}
          {periodType === "weekly" && <div><Label>Week starting</Label><Input type="date" value={week} onChange={(e) => setWeek(e.target.value)} className="mt-1.5" data-testid="week-input" /></div>}
          {periodType === "custom" && <>
            <div><Label>Start</Label><Input type="date" value={custStart} onChange={(e) => setCustStart(e.target.value)} className="mt-1.5" data-testid="custom-start" /></div>
            <div><Label>End</Label><Input type="date" value={custEnd} onChange={(e) => setCustEnd(e.target.value)} className="mt-1.5" data-testid="custom-end" /></div>
          </>}
        </div>
        <div className="min-w-[200px]">
          <Label>Location</Label>
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="mt-1.5" data-testid="location-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <SumCard icon={TrendingUp} label="Total Revenue" value={fmt(summary.total_revenue)} sub={`${summary.sessions} sessions · ${periodLabel}`} accent />
        <SumCard icon={IndianRupee} label="My Share" value={fmt(summary.my_share_amount)} sub={`${summary.my_share_percentage}% share`} />
        <SumCard icon={CheckCircle2} label="Already Settled" value={fmt(summary.already_settled)} />
        <SumCard icon={Clock} label="Pending" value={fmt(summary.pending)} />
        <SumCard icon={Handshake} label="Partner Share" value={fmt(summary.partner_share_amount)} sub={`${summary.partner_share_percentage}%`} />
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Status for {summary.location_scope}:</span>
        <StatusBadge status={summary.status} />
      </div>

      {/* Location / chair breakdown */}
      <div className="mt-6 rounded-2xl bg-card p-5 gold-border">
        <div className="mb-3 font-serif text-lg font-semibold">Revenue Breakdown</div>
        {summary.by_location.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No paid revenue in this period.</p>
        ) : summary.by_location.map((L) => (
          <div key={L.location_id} className="mb-2 rounded-xl bg-secondary/40">
            <button onClick={() => setExpanded((s) => ({ ...s, [L.location_id]: !s[L.location_id] }))}
              className="flex w-full items-center justify-between p-4 text-left" data-testid={`loc-row-${L.location_id}`}>
              <div className="flex items-center gap-2 font-semibold">
                {expanded[L.location_id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                {L.name}
              </div>
              <div className="text-sm"><span className="text-muted-foreground mr-3">{L.sessions} sessions</span><span className="gold-text font-bold">{fmt(L.revenue)}</span></div>
            </button>
            {expanded[L.location_id] && (
              <div className="px-4 pb-3">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Chair</th><th className="text-right">Sessions</th><th className="text-right">Revenue</th></tr></thead>
                  <tbody>
                    {L.chairs.map((c) => (
                      <tr key={c.chair_code} className="border-t border-border/40" data-testid={`chair-row-${c.chair_code}`}>
                        <td className="py-1.5 font-mono text-xs">{c.chair_code}</td>
                        <td className="text-right">{c.sessions}</td>
                        <td className="text-right font-medium">{fmt(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* History */}
      <div className="mt-6 rounded-2xl bg-card p-5 gold-border">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="font-serif text-lg font-semibold">Settlement History</div>
          <div className="flex gap-2">
            <Select value={hStatus} onValueChange={setHStatus}><SelectTrigger className="w-[150px]" data-testid="hist-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="fully_settled">Fully Settled</SelectItem><SelectItem value="reversal">Reversal</SelectItem></SelectContent></Select>
            <Select value={hMethod} onValueChange={setHMethod}><SelectTrigger className="w-[150px]" data-testid="hist-method-filter"><SelectValue placeholder="Method" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Methods</SelectItem>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_th]:px-2 [&_td]:px-2">
            <thead><tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="p-2">Settlement ID</th><th>Period</th><th className="text-right">Revenue</th><th className="text-right">My Share</th><th className="text-right">Settled</th><th className="text-right">Pending</th><th>Method</th><th>Date</th><th>Reference</th><th>Status</th><th>By</th>{canReverse && <th></th>}
            </tr></thead>
            <tbody>
              {history.length === 0 ? <tr><td colSpan={canReverse ? 12 : 11} className="py-8 text-center text-muted-foreground">No settlements recorded yet.</td></tr>
                : history.map((h) => (
                  <tr key={h.id} className="border-b border-border/40" data-testid={`hist-${h.settlement_id}`}>
                    <td className="p-2 font-mono text-xs">{h.settlement_id}</td>
                    <td className="text-xs">{fmtDate(h.start_date)}–{fmtDate(h.end_date)}<div className="text-muted-foreground">{h.location_name}</div></td>
                    <td className="text-right">{fmt(h.total_revenue)}</td>
                    <td className="text-right">{fmt(h.my_share_amount)}</td>
                    <td className="text-right font-semibold">{fmt(h.settled_amount)}</td>
                    <td className="text-right">{fmt(h.remaining_pending_amount)}</td>
                    <td className="text-xs">{methodLabel(h.payment_method)}</td>
                    <td className="text-xs">{fmtDate(h.settlement_date)}</td>
                    <td className="text-xs">{h.payment_reference || "—"}</td>
                    <td><StatusBadge status={h.status} /></td>
                    <td className="text-xs">{h.created_by}</td>
                    {canReverse && <td>{h.status !== "reversal" && !h.reversed && <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => reverse(h)} data-testid={`reverse-${h.settlement_id}`}><RotateCcw size={12} /></Button>}</td>}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {settleOpen && <SettleDialog summary={summary} periodParams={params()} onClose={() => setSettleOpen(false)} onDone={() => { setSettleOpen(false); refresh(); }} />}
    </div>
  );
}

function SettleDialog({ summary, periodParams, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = summary.pending;

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than zero."); return; }
    if (amt > pending + 0.01) { toast.error(`Settlement amount cannot exceed the pending amount of ${fmt(pending)}.`); return; }
    setBusy(true);
    try {
      await api.post("/settlement/settle", { ...periodParams, settled_amount: amt, payment_method: method, payment_reference: reference, settlement_date: date, notes });
      toast.success("Settlement recorded");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Settle Amount — {summary.location_scope}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-secondary/40 p-4 text-sm">
          <div className="text-muted-foreground">Period</div><div className="text-right">{fmtDate(summary.start_date)}–{fmtDate(summary.end_date)}</div>
          <div className="text-muted-foreground">Total Revenue</div><div className="text-right">{fmt(summary.total_revenue)}</div>
          <div className="text-muted-foreground">My Share ({summary.my_share_percentage}%)</div><div className="text-right">{fmt(summary.my_share_amount)}</div>
          <div className="text-muted-foreground">Already Settled</div><div className="text-right">{fmt(summary.already_settled)}</div>
          <div className="font-semibold gold-text">Pending</div><div className="text-right font-bold gold-text">{fmt(pending)}</div>
        </div>
        <div className="space-y-3">
          <div><Label>Amount Being Settled (₹)</Label><Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(pending)} className="mt-1.5" data-testid="settle-amount-input" /></div>
          <div><Label>Payment Method</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger className="mt-1.5" data-testid="settle-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Payment Reference / Transaction ID</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1.5" data-testid="settle-reference-input" /></div>
          <div><Label>Settlement Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="settle-date-input" /></div>
          <div><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" data-testid="settle-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || pending <= 0} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="confirm-settlement-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Settlement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SettlementOverviewCard({ onView }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/settlement/summary", { params: { period_type: "monthly", start: `${monthStr()}-01`, location_id: "all" } }).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="rounded-2xl bg-card p-6 gold-border" data-testid="overview-settlement-card">
      <div className="flex items-center gap-2"><Handshake className="gold-text" size={18} /><span className="overline-label">Share Settlement · This Month</span></div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-center">
        <div><div className="text-xs text-muted-foreground">My Share</div><div className="mt-1 text-lg font-bold">{fmt(d.my_share_amount)}</div></div>
        <div><div className="text-xs text-muted-foreground">Settled</div><div className="mt-1 text-lg font-bold text-emerald-400">{fmt(d.already_settled)}</div></div>
        <div><div className="text-xs text-muted-foreground">Pending</div><div className="mt-1 text-lg font-bold gold-text">{fmt(d.pending)}</div></div>
      </div>
      <Button variant="outline" className="mt-4 w-full gap-1.5 gold-border text-gold" onClick={onView} data-testid="overview-view-settlement">
        View Settlement
      </Button>
    </div>
  );
}
